import {  t  } from "@/client/libs/dummy-i18n";
import { CheckIcon, CopyIcon, GiftIcon, ShareNetworkIcon, UsersIcon } from "@phosphor-icons/react";
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label } from "@stackmorph/ui";
import { motion } from "framer-motion";
import { useState } from "react";

import { useUser } from "@/client/services/user";

export const ReferralDashboard = () => {
  const { user } = useUser();
  const [copied, setCopied] = useState(false);

  // Generate referral code from user ID
  const referralCode = user?.referralCode ?? "STACK" + (user?.id?.slice(0, 6).toUpperCase() ?? "XXXXXX");
  const referralLink = `${window.location.origin}/auth/register?ref=${referralCode}`;
  
  // Mock stats - in production, fetch from backend API
  const stats = {
    total: 0,
    completed: 0,
    pending: 0,
    rewardsEarned: 0,
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  const shareViaTwitter = () => {
    const text = `Check out StackMorph - the best resume builder! 🚀`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralLink)}`;
    window.open(url, '_blank');
  };

  const shareViaEmail = () => {
    const subject = "Try StackMorph Resume Builder";
    const body = `I've been using StackMorph to create amazing resumes. Try it out: ${referralLink}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const shareViaWhatsApp = () => {
    const text = `Check out StackMorph Resume Builder! ${referralLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div className="h-full">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="h-full"
      >
        <Card className="h-full border-purple-500/20 bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-orange-500/10 backdrop-blur-xl">
          <CardHeader className="pb-4">
            <div className="space-y-4">
              <div>
                <CardTitle className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-2xl font-bold text-transparent">
                  {t`Referral Program`}
                </CardTitle>
                <CardDescription className="mt-1 text-base">
                  {t`Earn Premium free by inviting friends`}
                </CardDescription>
              </div>

              <div className="flex items-center gap-3 rounded-xl bg-background/50 p-4 backdrop-blur-sm">
                <div className="rounded-full bg-gradient-to-br from-purple-500 to-pink-500 p-3">
                  <GiftIcon size={24} className="text-white" weight="fill" />
                </div>
                <div>
                  <h3 className="font-semibold">{t`Earn Premium Free`}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t`Get 1 month Premium per 3 successful referrals`}
                  </p>
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Referral Link */}
            <div className="space-y-3">
              <Label htmlFor="referral-link" className="text-sm font-semibold">
                {t`Your Unique Referral Link`}
              </Label>
              <div className="flex gap-2">
                <Input 
                  readOnly 
                  id="referral-link" 
                  value={referralLink} 
                  className="flex-1 bg-background/80 font-mono text-sm"
                />
                <Button variant={copied ? "primary" : "outline"} size="icon" className="shrink-0" onClick={handleCopy}>
                  {copied ? <CheckIcon size={18} className="text-white" /> : <CopyIcon size={18} />}
                </Button>
              </div>
            </div>

            {/* Social Sharing */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold">{t`Share via`}</Label>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1 gap-2 bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20" 
                  onClick={shareViaTwitter}
                >
                  <ShareNetworkIcon size={18} />
                  Twitter
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1 gap-2 bg-[#25D366]/10 hover:bg-[#25D366]/20" 
                  onClick={shareViaWhatsApp}
                >
                  <ShareNetworkIcon size={18} />
                  WhatsApp
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1 gap-2" 
                  onClick={shareViaEmail}
                >
                  <ShareNetworkIcon size={18} />
                  Email
                </Button>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-4">
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="rounded-xl border border-border/50 bg-background/80 p-4 backdrop-blur-sm"
              >
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <UsersIcon size={16} />
                  {t`Total`}
                </div>
                <div className="mt-2 text-3xl font-bold">{stats.total}</div>
              </motion.div>

              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="rounded-xl border border-green-500/30 bg-green-500/10 p-4"
              >
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <CheckIcon size={16} weight="bold" />
                  {t`Completed`}
                </div>
                <div className="mt-2 text-3xl font-bold text-green-600 dark:text-green-400">{stats.completed}</div>
              </motion.div>

              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="rounded-xl border border-purple-500/30 bg-purple-500/10 p-4"
              >
                <div className="flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400">
                  <GiftIcon size={16} weight="fill" />
                  {t`Rewards`}
                </div>
                <div className="mt-2 text-3xl font-bold text-purple-600 dark:text-purple-400">
                  {stats.rewardsEarned > 0 ? `${stats.rewardsEarned}d` : "0"}
                </div>
              </motion.div>
            </div>

            {/* Progress indicator */}
            {stats.completed > 0 && stats.completed % 3 !== 0 && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="rounded-xl border border-purple-500/30 bg-gradient-to-r from-purple-500/20 to-pink-500/20 p-4"
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">
                    {t`${3 - (stats.completed % 3)} more ${3 - (stats.completed % 3) === 1 ? "referral" : "referrals"} until next reward! 🎉`}
                  </p>
                  <div className="text-2xl">
                    {stats.completed % 3}/3
                  </div>
                </div>
              </motion.div>
            )}

            {/* How it works */}
            {stats.completed === 0 && (
              <div className="rounded-xl border border-border/50 bg-background/50 p-6">
                <h4 className="mb-4 flex items-center gap-2 text-lg font-semibold">
                  <GiftIcon size={20} className="text-purple-500" weight="fill" />
                  {t`How it works`}
                </h4>
                <ul className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-500/20 text-xs font-bold text-purple-600">1</div>
                    <span>{t`Share your unique referral link with friends via social media or email`}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-500/20 text-xs font-bold text-purple-600">2</div>
                    <span>{t`They sign up using your link and create their first professional resume`}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-500/20 text-xs font-bold text-purple-600">3</div>
                    <span>{t`For every 3 successful referrals, you get 30 days of Premium absolutely free!`}</span>
                  </li>
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};
